import React, { useMemo, useState } from 'react';
import { Flex } from '@/ui/layout';
import { UploadFilesModal } from '@/components/common/modals/UploadFiles';
import { dateString, getFileSrc } from '@/utils/helpers/common';
import { ExternalLink } from '@/ui/atoms/Link';
import { useTranslation } from 'react-i18next';
import { Button } from 'antd';
import { Text } from '@/ui/typography';

export const DeepCheckView = ({ viewMode, deepCheckData, initCheck, uploadReport }) => {
  const { t } = useTranslation();
  const [uploadFileId, setUploadFileId] = useState(-1);

  const deepCheck = useMemo(() => {
    if (deepCheckData) {
      return {
        kycPending: deepCheckData.fullKyc?.lastPending,
        kycChecked: deepCheckData.fullKyc?.lastChecked,
        sanctionsPending: deepCheckData.sanctions?.lastPending,
        sanctionsChecked: deepCheckData.sanctions?.lastChecked
      };
    }

    return {};
  }, [deepCheckData]);

  return (
    <Flex vertical gap={24} wrap>
      <Flex vertical align="flex-end">
        {deepCheck.kycPending && (
          <Flex $marginBottom={10} wrap>
            <Text $fontSize={14} type='secondary'>
              {t('Requested')}
              <strong> {dateString(new Date(deepCheck.kycPending.createdAt))}</strong>
            </Text>
          </Flex>
        )}

        <Flex gap={16} align="center" style={{ width: '100%' }} justify="space-between">
          <Text>
            {t('Deep KYC status')}:&ensp;
            {deepCheck.kycChecked ? (
              <ExternalLink href={getFileSrc(deepCheck.kycChecked?.documentURL)}>
                {t('Report from')} {dateString(new Date(deepCheck.kycChecked.updatedAt))}
              </ExternalLink>
            ) : (
              <strong>{t('N/A')}</strong>
            )}
          </Text>

          {viewMode === 'admin' && (
            <Button
              onClick={() => initCheck('FULL_KYC')}
              disabled={deepCheck.kycPending}
              type='default'
            >
              {t('Request deep KYC')}
            </Button>
          )}
          {viewMode === 'superAdmin' && (
            <Button
              onClick={() => setUploadFileId(deepCheck.kycPending?.id)}
              disabled={!deepCheck.kycPending}
              type='default'
            >
              {t('Upload report for deep KYC')}
            </Button>
          )}
        </Flex>
      </Flex>

      <Flex vertical>
        {deepCheck.sanctionsPending && (
          <Text $fontSize={14} type='secondary'>
            {t('Requested')}
            <strong> {dateString(new Date(deepCheck.sanctionsPending.createdAt))}</strong>
          </Text>
        )}

        <Flex gap={16} align="center" style={{ width: '100%' }} justify="space-between">
          <Text>
            {t('Sanctions check status')}:&ensp;
            {deepCheck.sanctionsChecked ? (
              <ExternalLink href={getFileSrc(deepCheck.sanctionsChecked?.documentURL)}>
                {t('Report from')} {dateString(new Date(deepCheck.sanctionsChecked.updatedAt))}
              </ExternalLink>
            ) : (
              <strong>{t('N/A')}</strong>
            )}
          </Text>

          {viewMode === 'admin' && (
            <Button
              onClick={() => initCheck('SANCTIONS')}
              disabled={deepCheck.sanctionsPending}
              type='default'
            >
              {t('Sanctions check')}
            </Button>
          )}
          {viewMode === 'superAdmin' && (
            <Button
              onClick={() => setUploadFileId(deepCheck.sanctionsPending?.id)}
              disabled={!deepCheck.sanctionsPending}
              type='default'
            >
              {t('Upload report for sanctions check')}
            </Button>
          )}
        </Flex>
      </Flex>

      <UploadFilesModal
        open={uploadFileId !== -1}
        onClose={() => setUploadFileId(-1)}
        onSubmit={(files) => uploadReport({ checkId: uploadFileId, file: files[0] })}
      />
    </Flex>
  );
};
